// src/services/BookingService.js - Enhanced with booking management CRUD operations
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import { ConflictResolutionService } from './ConflictResolutionService';
import { BookingConflictEnhancer } from './enhancers/BookingConflictEnhancer';
import { ErrorTrackingService } from './ErrorTrackingService';
import { FirebaseService } from './FirebaseService';
import { NotificationService } from './NotificationService';

export class BookingService {

  // ==========================================
  // BOOKING DATA RETRIEVAL (Existing methods)
  // ==========================================
  
  static async getAllBusinessBookings(businessId, options = {}) {
    try {
      const { db } = FirebaseService.getInstances();
      const bookings = [];
      
      // Get all customers for this business
      const customersRef = collection(db, 'businesses', businessId, 'customers');
      const customersSnap = await getDocs(customersRef);
      
      // For each customer, get their bookings
      for (const customerDoc of customersSnap.docs) {
        const customerId = customerDoc.id;
        const customerData = customerDoc.data();
        
        // Get bookings for this customer
        let bookingsQuery = collection(db, 'businesses', businessId, 'customers', customerId, 'bookings');
        
        // Apply filters if provided
        if (options.startDate) {
          bookingsQuery = query(bookingsQuery, where('booking_date', '>=', options.startDate));
        }
        if (options.endDate) {
          bookingsQuery = query(bookingsQuery, where('booking_date', '<=', options.endDate));
        }
        if (options.status) {
          bookingsQuery = query(bookingsQuery, where('status', '==', options.status));
        }
        
        // Order by booking date
        bookingsQuery = query(bookingsQuery, orderBy('booking_date', 'desc'));
        
        const bookingsSnap = await getDocs(bookingsQuery);
        
        // Process each booking
        bookingsSnap.docs.forEach(bookingDoc => {
          const bookingData = bookingDoc.data();
          
          bookings.push({
            id: bookingDoc.id,
            customerId: customerId,
            customerName: customerData.name || customerData.customer_name || 'Unknown',
            customerPhone: customerData.phone || customerData.phone_number || '',
            customerEmail: customerData.email || '',
            ...bookingData
          });
        });
      }
      
      // Sort all bookings by date
      bookings.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      return {
        success: true,
        bookings: bookings,
        total: bookings.length
      };
      
    } catch (error) {
      console.error('Failed to get business bookings:', error);
      return {
        success: false,
        error: error.message,
        bookings: [],
        total: 0
      };
    }
  }

  // ==========================================
  // BOOKING NOTIFICATIONS
  // ==========================================
    // NEW: Enhanced createBooking with optional notifications
  static async createBookingWithNotifications(businessId, bookingData, options = {}) {
    try {
      // 1. Call original createBooking method (preserves existing behavior)
      const bookingResult = await this.createBooking(businessId, bookingData);
      
      if (!bookingResult.success) {
        return bookingResult; // Return original error unchanged
      }

      // 2. Send notifications if enabled (NEW enhancement)
      if (options.sendNotifications !== false) { // Default to true
        try {
          await this.sendBookingNotifications('booking_created', {
            businessId,
            bookingId: bookingResult.bookingId,
            customerId: bookingResult.customerId,
            bookingData
          });
        } catch (notificationError) {
          // CRITICAL: Don't let notification failures break booking creation
          console.warn('Notification failed but booking succeeded:', notificationError);
          ErrorTrackingService.captureError(notificationError, {
            service: 'BookingService',
            method: 'createBookingWithNotifications',
            bookingId: bookingResult.bookingId,
            level: 'warning'
          });
        }
      }

      // 3. Return original result with notification status (non-breaking)
      return {
        ...bookingResult,
        notificationsSent: options.sendNotifications !== false
      };

    } catch (error) {
      console.error('Enhanced booking creation failed:', error);
      ErrorTrackingService.captureError(error, {
        service: 'BookingService',
        method: 'createBookingWithNotifications',
      });
      
      // Return same error format as original method
      return {
        success: false,
        error: error.message
      };
    }
  }

  // NEW: Enhanced updateBooking with optional notifications
  static async updateBookingWithNotifications(businessId, customerId, bookingId, updateData, options = {}) {
    try {
      // 1. Call original updateBooking method
      const updateResult = await this.updateBooking(businessId, customerId, bookingId, updateData);
      
      if (!updateResult.success) {
        return updateResult;
      }

      // 2. Send notifications if enabled
      if (options.sendNotifications !== false) {
        try {
          await this.sendBookingNotifications('booking_updated', {
            businessId,
            bookingId,
            customerId,
            updateData
          });
        } catch (notificationError) {
          console.warn('Update notification failed but booking updated:', notificationError);
          ErrorTrackingService.captureError(notificationError, {
            service: 'BookingService',
            method: 'updateBookingWithNotifications',
            bookingId,
            level: 'warning'
          });
        }
      }

      return {
        ...updateResult,
        notificationsSent: options.sendNotifications !== false
      };

    } catch (error) {
      console.error('Enhanced booking update failed:', error);
      ErrorTrackingService.captureError(error, {
        service: 'BookingService',
        method: 'updateBookingWithNotifications',
        bookingId
      });
      
      return {
        success: false,
        error: error.message
      };
    }
  }

  // NEW: Enhanced cancelBooking with optional notifications
  static async cancelBookingWithNotifications(businessId, customerId, bookingId, reason = '', options = {}) {
    try {
      // 1. Call original cancelBooking method
      const cancelResult = await this.cancelBooking(businessId, customerId, bookingId, reason);
      
      if (!cancelResult.success) {
        return cancelResult;
      }

      // 2. Send notifications if enabled
      if (options.sendNotifications !== false) {
        try {
          await this.sendBookingNotifications('booking_cancelled', {
            businessId,
            bookingId,
            customerId,
            reason
          });
        } catch (notificationError) {
          console.warn('Cancellation notification failed but booking cancelled:', notificationError);
          ErrorTrackingService.captureError(notificationError, {
            service: 'BookingService',
            method: 'cancelBookingWithNotifications',
            bookingId,
            level: 'warning'
          });
        }
      }

      return {
        ...cancelResult,
        notificationsSent: options.sendNotifications !== false
      };

    } catch (error) {
      console.error('Enhanced booking cancellation failed:', error);
      ErrorTrackingService.captureError(error, {
        service: 'BookingService',
        method: 'cancelBookingWithNotifications',
        bookingId
      });
      
      return {
        success: false,
        error: error.message
      };
    }
  }

  // NEW: Notification sending helper
  static async sendBookingNotifications(type, data) {
    try {
      // Get business and customer data for notifications
      const [businessData, customerData, bookingData] = await Promise.all([
        this.getBusinessData(data.businessId),
        this.getCustomerData(data.businessId, data.customerId),
        this.getBookingData(data.businessId, data.customerId, data.bookingId)
      ]);

      if (!businessData || !customerData || !bookingData) {
        throw new Error('Missing required data for notifications');
      }

      // Prepare notification data
      const notificationData = {
        businessId: data.businessId,
        type,
        recipient: {
          email: customerData.email,
          phone: customerData.phone,
          name: customerData.name
        },
        message: this.generateNotificationMessage(type, bookingData, businessData),
        bookingId: data.bookingId,
        customerId: data.customerId,
        
        // Additional data for rich notifications
        customerName: customerData.name,
        serviceName: bookingData.serviceName || bookingData.service_name,
        bookingDate: bookingData.bookingDate || bookingData.booking_date,
        time: bookingData.time,
        businessName: businessData.business_name || businessData.businessName,
        businessPhone: businessData.phone_number || businessData.phoneNumber,
        businessAddress: businessData.address,
      };

      // Send through NotificationService
      const result = await NotificationService.sendNotification(notificationData);
      
      console.log(`📧 Notifications sent for ${type}:`, result.enhancedChannels || ['in-app']);
      
      return result;

    } catch (error) {
      console.error('Failed to send booking notifications:', error);
      ErrorTrackingService.captureError(error, {
        service: 'BookingService',
        method: 'sendBookingNotifications',
        notificationType: type,
      });
      throw error;
    }
  }

  // NEW: Helper methods for notification data
  static async getBusinessData(businessId) {
    try {
      return await FirebaseService.getBusinessProfile(businessId);
    } catch (error) {
      console.error('Failed to get business data:', error);
      return null;
    }
  }

  static async getCustomerData(businessId, customerId) {
    try {
      const customerProfile = await FirebaseService.getCustomerProfile(businessId, customerId);
      return customerProfile.customer || null;
    } catch (error) {
      console.error('Failed to get customer data:', error);
      return null;
    }
  }

  static async getBookingData(businessId, customerId, bookingId) {
    try {
      const allBookings = await this.getAllBusinessBookings(businessId);
      if (!allBookings.success) {
        throw new Error('Failed to get bookings');
      }
      
      const booking = allBookings.bookings.find(b => 
        b.id === bookingId && b.customerId === customerId
      );
      
      return booking || null;
    } catch (error) {
      console.error('Failed to get booking data:', error);
      return null;
    }
  }

  // NEW: Generate notification messages
  static generateNotificationMessage(type, bookingData, businessData) {
    const messages = {
      booking_created: `Your appointment for ${bookingData.serviceName || bookingData.service_name} on ${bookingData.bookingDate || bookingData.booking_date} at ${bookingData.time} has been confirmed.`,
      
      booking_updated: `Your appointment for ${bookingData.serviceName || bookingData.service_name} has been updated to ${bookingData.bookingDate || bookingData.booking_date} at ${bookingData.time}.`,
      
      booking_cancelled: `Your appointment for ${bookingData.serviceName || bookingData.service_name} on ${bookingData.bookingDate || bookingData.booking_date} has been cancelled.`
    };

    return messages[type] || messages.booking_created;
  }

  // NEW: Batch notification sending (for multiple bookings)
  static async sendBatchNotifications(notifications) {
    try {
      const results = [];
      
      // Process notifications in batches to avoid overwhelming the system
      const batchSize = 5;
      for (let i = 0; i < notifications.length; i += batchSize) {
        const batch = notifications.slice(i, i + batchSize);
        
        const batchPromises = batch.map(async (notification) => {
          try {
            return await this.sendBookingNotifications(
              notification.type, 
              notification.data
            );
          } catch (error) {
            console.warn(`Batch notification failed for ${notification.data.bookingId}:`, error);
            return { success: false, error: error.message };
          }
        });

        const batchResults = await Promise.allSettled(batchPromises);
        results.push(...batchResults);

        // Small delay between batches
        if (i + batchSize < notifications.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      const successCount = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
      const failureCount = results.length - successCount;

      console.log(`📊 Batch notifications complete: ${successCount} success, ${failureCount} failed`);

      return {
        success: true,
        total: results.length,
        successCount,
        failureCount,
        results
      };

    } catch (error) {
      console.error('Batch notification sending failed:', error);
      ErrorTrackingService.captureError(error, {
        service: 'BookingService',
        method: 'sendBatchNotifications',
      });
      
      return {
        success: false,
        error: error.message
      };
    }
  }

  // NEW: Notification preferences management
  static async updateNotificationPreferences(businessId, customerId, preferences) {
    try {
      const { auth, db } = FirebaseService.getInstances();
      // Store customer notification preferences
      const customerRef = doc(db, 'businesses', businessId, 'customers', customerId);
      await updateDoc(customerRef, {
        notification_preferences: {
          email: preferences.email !== false,
          sms: preferences.sms !== false,
          whatsapp: preferences.whatsapp !== false,
          in_app: preferences.in_app !== false,
          updated_at: new Date().toISOString()
        }
      });

      console.log('✅ Notification preferences updated for customer:', customerId);
      
      return {
        success: true,
        preferences
      };

    } catch (error) {
      console.error('Failed to update notification preferences:', error);
      ErrorTrackingService.captureError(error, {
        service: 'BookingService',
        method: 'updateNotificationPreferences',
        customerId
      });
      
      return {
        success: false,
        error: error.message
      };
    }
  }

  // NEW: Get notification status/history
  static async getNotificationHistory(businessId, bookingId) {
    try {
      const { auth, db } = FirebaseService.getInstances();
      // Get notification history from Firestore
      const notificationsQuery = query(
        collection(db, 'businesses', businessId, 'notifications'),
        where('bookingId', '==', bookingId),
        orderBy('created_at', 'desc')
      );
      
      const notificationsSnap = await getDocs(notificationsQuery);
      const notifications = notificationsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      return {
        success: true,
        notifications
      };

    } catch (error) {
      console.error('Failed to get notification history:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

    // ==========================================
  // CUSTOMER ID GENERATION (ADDED - Missing Method)
  // ==========================================

  /**
   * Generate consistent customer ID from booking data
   */
  static generateCustomerId(bookingData) {
    // Use existing customer ID if provided
    if (bookingData.customerId) {
      return bookingData.customerId;
    }
    
    // Use phone number as consistent identifier (normalized)
    if (bookingData.customerPhone || bookingData.customer_whatsapp) {
      const phone = (bookingData.customerPhone || bookingData.customer_whatsapp)
        .replace(/\D/g, ''); // Remove non-digits
      return `customer_phone_${phone}`;
    }
    
    // Use email if available
    if (bookingData.customerEmail) {
      const emailId = bookingData.customerEmail.toLowerCase()
        .replace(/[^a-z0-9]/g, '_'); // Sanitize for Firebase key
      return `customer_email_${emailId}`;
    }
    
    // Use customer name + timestamp as last resort
    if (bookingData.customerName || bookingData.customer_name) {
      const name = (bookingData.customerName || bookingData.customer_name)
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '_')
        .substring(0, 20); // Limit length
      return `customer_${name}_${Date.now()}`;
    }
    
    // Final fallback
    console.warn('No customer identifier found, using timestamp fallback');
    return `customer_unknown_${Date.now()}`;
  }

  /**
   * Find customer by phone number
   */
  static async findCustomerByPhone(businessId, phoneNumber) {
    try {
      const customersRef = collection(FirebaseService.getInstances().db, 'businesses', businessId, 'customers');
      const phoneQuery = query(customersRef, where('phone', '==', phoneNumber));
      const querySnapshot = await getDocs(phoneQuery);
      
      if (!querySnapshot.empty) {
        const customerDoc = querySnapshot.docs[0];
        return {
          id: customerDoc.id,
          ...customerDoc.data()
        };
      }
      
      return null; // Customer not found
    } catch (error) {
      console.error('Failed to find customer by phone:', error);
      return null;
    }
  }

  /**
   * Create new customer (ADDED - Supporting Method)
   */
  static async createCustomer(businessId, customerData) {
    try {
      const customerId = this.generateCustomerId({
        customerName: customerData.name,
        customerPhone: customerData.phone,
        customerEmail: customerData.email
      });

      const customerRef = doc(FirebaseService.getInstances().db, 'businesses', businessId, 'customers', customerId);
      
      const customerDocument = {
        id: customerId,
        name: customerData.name || 'Unknown Customer',
        phone: customerData.phone || '',
        email: customerData.email || '',
        whatsapp: customerData.whatsapp || customerData.phone || '',
        business_id: businessId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        booking_count: 0
      };
      
      await setDoc(customerRef, customerDocument);
      
      console.log('Customer created:', customerId);
      return {
        success: true,
        customerId: customerId,
        customer: customerDocument
      };
    } catch (error) {
      console.error('Failed to create customer:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }


  // ==========================================
  // BOOKING CRUD OPERATIONS (New methods)
  // ==========================================
  /**
   * Create a new booking - FIXED to remove duplicate calendar sync
   */
  static async createBookingAtomic(businessId, bookingData, transaction) {
    try {
      if (!transaction) {
        throw new Error('Firestore transaction instance is required for atomic booking creation');
      }

      const { db } = FirebaseService.getInstances();

      const customerName = bookingData.customerName || bookingData.customer_name;
      const customerPhone = bookingData.customerPhone || bookingData.customer_phone;
      const customerEmail = bookingData.customerEmail || bookingData.customer_email || '';
      const customerWhatsapp = bookingData.customerWhatsapp || bookingData.customer_whatsapp || customerPhone;
      const serviceName = bookingData.serviceName || bookingData.service_name || bookingData.service_type;
      const bookingDate = bookingData.bookingDate || bookingData.booking_date || bookingData.date;
      const bookingTime = bookingData.time || bookingData.booking_time;
      const status = bookingData.status || 'confirmed';

      if (!customerName || !customerPhone || !serviceName) {
        throw new Error('Customer name, phone, and service are required');
      }

      if (!bookingDate || !bookingTime) {
        throw new Error('Booking date and time are required');
      }

      const parsedDuration = parseInt(
        bookingData.duration || bookingData.duration_minutes || bookingData.durationMinutes || 60,
        10
      );
      const duration = Number.isFinite(parsedDuration) && parsedDuration > 0 ? parsedDuration : 60;

      const bookingDateTime = new Date(`${bookingDate}T${bookingTime}`);
      if (isNaN(bookingDateTime.getTime())) {
        throw new Error('Invalid booking date/time');
      }

      const bookingEnd = new Date(bookingDateTime.getTime() + duration * 60000);
      const nowIso = new Date().toISOString();

      let customerId = bookingData.customerId || bookingData.customer_id;
      if (!customerId) {
        customerId = this.generateCustomerId({
          ...bookingData,
          customerPhone
        });
      }

      const customerRef = doc(db, 'businesses', businessId, 'customers', customerId);
      const customerSnap = await transaction.get(customerRef);

      if (!customerSnap.exists()) {
        transaction.set(customerRef, {
          id: customerId,
          name: customerName,
          phone: customerPhone,
          email: customerEmail,
          whatsapp: customerWhatsapp,
          business_id: businessId,
          created_at: nowIso,
          updated_at: nowIso,
          booking_count: 0
        });
      } else {
        const customerData = customerSnap.data() || {};
        const customerUpdates = {};

        if (customerName && customerName !== customerData.name) customerUpdates.name = customerName;
        if (customerPhone && customerPhone !== customerData.phone) customerUpdates.phone = customerPhone;
        if (customerEmail && customerEmail !== customerData.email) customerUpdates.email = customerEmail;
        if (customerWhatsapp && customerWhatsapp !== customerData.whatsapp) customerUpdates.whatsapp = customerWhatsapp;

        if (Object.keys(customerUpdates).length > 0) {
          customerUpdates.updated_at = nowIso;
          transaction.update(customerRef, customerUpdates);
        }
      }

      const bookingsCollection = collection(db, 'businesses', businessId, 'customers', customerId, 'bookings');
      const bookingRef = doc(bookingsCollection);

      const servicePrice = parseFloat(bookingData.servicePrice || bookingData.service_price || 0);
      const conflictResolutionMeta = bookingData.conflict_resolution || {
        method: 'transactional',
        checkedAt: nowIso
      };

      const newBooking = {
        business_id: businessId,
        customer_name: customerName,
        customer_whatsapp: customerWhatsapp,
        date: bookingDate,
        booking_date: bookingDate,
        time: bookingTime,
        event_start: bookingDateTime.toISOString(),
        event_end: bookingEnd.toISOString(),
        service_type: serviceName,
        service_name: serviceName,
        service_price: servicePrice,
        service_duration: duration,
        duration_minutes: duration,
        status,
        summary: bookingData.summary || `${serviceName} - ${customerName}`,
        notes: bookingData.notes || '',
        created_at: nowIso,
        updated_at: nowIso,
        created_by: bookingData.created_by || 'dashboard_atomic',
        source: bookingData.source || 'transactional',
        calendar_sync_status: 'pending',
        conflict_resolution: conflictResolutionMeta
      };

      transaction.set(bookingRef, newBooking);

      return {
        success: true,
        bookingId: bookingRef.id,
        customerId,
        booking: {
          ...newBooking,
          id: bookingRef.id,
          customerId
        }
      };

    } catch (error) {
      console.error('Atomic booking creation failed:', error);
      ErrorTrackingService.captureError(error, {
        service: 'BookingService',
        method: 'createBookingAtomic',
        businessId
      });

      return {
        success: false,
        error: error.message
      };
    }
  }

  static async createBooking(businessId, bookingData) {
    try {
      const { db } = FirebaseService.getInstances();
      
      console.log('Creating booking in Firebase only (calendar sync handled by useBookings)');
      
      // Validate required fields
      if (!bookingData.customerName || !bookingData.customerPhone || !bookingData.serviceName) {
        throw new Error('Customer name, phone, and service are required');
      }
      
      if (!bookingData.bookingDate || !bookingData.time) {
        throw new Error('Booking date and time are required');
      }

      // Generate customer ID using the method we just added
      let customerId = this.generateCustomerId(bookingData);
      
      // Check if customer exists, create if needed
      const existingCustomer = await this.findCustomerByPhone(businessId, bookingData.customerPhone);
      
      if (existingCustomer) {
        customerId = existingCustomer.id;
      } else {
        // Create new customer
        const customerResult = await this.createCustomer(businessId, {
          name: bookingData.customerName,
          phone: bookingData.customerPhone,
          email: bookingData.customerEmail || '',
          whatsapp: bookingData.customerWhatsapp || bookingData.customerPhone
        });
        
        if (!customerResult.success) {
          throw new Error('Failed to create customer: ' + customerResult.error);
        }
        
        customerId = customerResult.customerId;
      }

      // Prepare booking document
      const now = new Date();
      const bookingDateTime = new Date(`${bookingData.bookingDate}T${bookingData.time}`);
      
      const newBooking = {
        business_id: businessId,
        customer_name: bookingData.customerName,
        customer_whatsapp: bookingData.customerWhatsapp || bookingData.customerPhone,
        date: bookingData.bookingDate,
        booking_date: bookingData.bookingDate,
        time: bookingData.time,
        event_start: bookingDateTime.toISOString(),
        event_end: new Date(bookingDateTime.getTime() + (bookingData.duration || 60) * 60000).toISOString(),
        service_type: bookingData.serviceName,
        service_name: bookingData.serviceName,
        service_price: parseFloat(bookingData.servicePrice || 0),
        service_duration: parseInt(bookingData.duration || 60),
        duration_minutes: parseInt(bookingData.duration || 60),
        status: 'confirmed',
        summary: `${bookingData.serviceName} - ${bookingData.customerName}`,
        notes: bookingData.notes || '',
        created_at: now.toISOString(),
        updated_at: now.toISOString(),
        created_by: 'dashboard',
        source: 'manual',
        
        // Calendar sync will be handled by useBookings hook
        calendar_sync_status: 'pending'
      };

      // Add booking to Firebase
      const bookingRef = await addDoc(
        collection(db, 'businesses', businessId, 'customers', customerId, 'bookings'),
        newBooking
      );

      console.log('Booking created in Firebase:', bookingRef.id);

      // Calendar sync is now handled by useBookings hook to prevent duplicates

      return {
        success: true,
        bookingId: bookingRef.id,
        customerId: customerId,
        booking: { 
          ...newBooking, 
          id: bookingRef.id,
          customerId: customerId 
        }
      };

    } catch (error) {
      console.error('Failed to create booking:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }


    /**
     * Update an existing booking
     */
  static async updateBooking(businessId, customerId, bookingId, updateData) {
    try {
      const { db } = FirebaseService.getInstances();
      
      // Get current booking data
      const bookingRef = doc(db, 'businesses', businessId, 'customers', customerId, 'bookings', bookingId);
      const bookingSnap = await getDoc(bookingRef);
      
      if (!bookingSnap.exists()) {
        throw new Error('Booking not found');
      }

      const currentBooking = bookingSnap.data();
      
      // Prepare update data
      const updates = {
        ...updateData,
        updated_at: new Date().toISOString(),
        updated_by: 'dashboard'
      };

      // If date/time is being updated, recalculate start/end times
      if (updateData.bookingDate || updateData.time) {
        const bookingDate = updateData.bookingDate || currentBooking.booking_date || currentBooking.date;
        const bookingTime = updateData.time || currentBooking.time;
        const duration = updateData.duration || currentBooking.duration_minutes || 60;
        
        const bookingDateTime = new Date(`${bookingDate}T${bookingTime}`);
        
        updates.event_start = bookingDateTime.toISOString();
        updates.event_end = new Date(bookingDateTime.getTime() + duration * 60000).toISOString();
        updates.date = bookingDate;
        updates.booking_date = bookingDate;
        
        // Mark for calendar re-sync if time/date changed
        updates.calendar_sync_status = 'pending';
      }

      // Update summary if service or customer name changed
      if (updateData.serviceName || updateData.customerName) {
        const serviceName = updateData.serviceName || currentBooking.service_name || currentBooking.service_type;
        const customerName = updateData.customerName || currentBooking.customer_name;
        updates.summary = `${serviceName} - ${customerName}`;
        
        // Mark for calendar re-sync if details changed
        updates.calendar_sync_status = 'pending';
      }

      // Update Firebase
      await updateDoc(bookingRef, updates);

      // Get updated booking data
      const updatedBookingSnap = await getDoc(bookingRef);
      const updatedBooking = { 
        ...updatedBookingSnap.data(), 
        id: bookingId, 
        customerId 
      };

      console.log('Booking updated in Firebase:', bookingId);

      // Calendar sync is now handled by useBookings hook to prevent duplicates

      return {
        success: true,
        booking: updatedBooking
      };

    } catch (error) {
      console.error('Failed to update booking:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }



  /**
   * Cancel a booking (soft delete - updates status to 'cancelled')
   */
  static async cancelBooking(businessId, customerId, bookingId, reason = '') {
    try {
      const result = await this.updateBooking(businessId, customerId, bookingId, {
        status: 'cancelled',
        cancelled_at: new Date().toISOString(),
        cancellation_reason: reason
      });

      if (result.success) {
        // Sync cancellation to Google Calendar
        try {
          await this.syncBookingToCalendar(businessId, result.booking, 'cancel');
        } catch (calendarError) {
          console.warn('Calendar sync failed:', calendarError);
        }
      }

      return result;

    } catch (error) {
      console.error('Failed to cancel booking:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Delete a booking permanently (hard delete)
   */
  static async deleteBooking(businessId, customerId, bookingId, reason = '') {
    try {
      const { db } = FirebaseService.getInstances();
      
      const bookingRef = doc(db, 'businesses', businessId, 'customers', customerId, 'bookings', bookingId);
      await deleteDoc(bookingRef);

      console.log('Booking deleted from Firebase:', bookingId);

      return { 
        success: true,
        message: 'Booking deleted successfully'
      };
    } catch (error) {
      console.error('Failed to delete booking:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // ==========================================
  // CUSTOMER MANAGEMENT METHODS
  // ==========================================

  static async findCustomerByPhone(businessId, phoneNumber) {
    try {
      const { db } = FirebaseService.getInstances();
      
      const customersRef = collection(db, 'businesses', businessId, 'customers');
      const phoneQuery = query(customersRef, where('phone', '==', phoneNumber));
      const querySnap = await getDocs(phoneQuery);
      
      if (!querySnap.empty) {
        const customerDoc = querySnap.docs[0];
        return {
          id: customerDoc.id,
          ...customerDoc.data()
        };
      }
      
      return null;
    } catch (error) {
      console.error('Failed to find customer:', error);
      return null;
    }
  }

  static async createCustomer(businessId, customerData) {
    try {
      const { db } = FirebaseService.getInstances();
      
      const newCustomer = {
        name: customerData.name,
        phone: customerData.phone,
        email: customerData.email || '',
        whatsapp: customerData.whatsapp || customerData.phone,
        customer_name: customerData.name,
        phone_number: customerData.phone,
        customer_whatsapp: customerData.whatsapp || customerData.phone,
        created_at: new Date().toISOString(),
        source: 'dashboard'
      };

      const customerRef = await addDoc(
        collection(db, 'businesses', businessId, 'customers'),
        newCustomer
      );

      return {
        success: true,
        customerId: customerRef.id,
        customer: { ...newCustomer, id: customerRef.id }
      };

    } catch (error) {
      console.error('Failed to create customer:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // ==========================================
  // GOOGLE CALENDAR SYNC METHODS
  // ==========================================

  static async syncBookingToCalendar(businessId, bookingData, action) {
    try {
      // Check if calendar integration exists
      const calendarIntegration = await FirebaseService.checkCalendarIntegration(businessId);
      
      if (!calendarIntegration.hasCalendar) {
        console.warn('No calendar integration found, skipping sync');
        return { success: false, reason: 'No calendar integration' };
      }

      // Prepare calendar event data
      const eventData = {
        summary: bookingData.summary || `${bookingData.service_name} - ${bookingData.customer_name}`,
        start: {
          dateTime: bookingData.event_start,
          timeZone: 'America/Toronto' // Adjust based on business timezone
        },
        end: {
          dateTime: bookingData.event_end,
          timeZone: 'America/Toronto'
        },
        description: [
          `Service: ${bookingData.service_name || bookingData.service_type}`,
          `Customer: ${bookingData.customer_name}`,
          `Phone: ${bookingData.customer_whatsapp}`,
          bookingData.notes ? `Notes: ${bookingData.notes}` : '',
          `Booking ID: ${bookingData.id}`,
          'Created via Booker.Chat Dashboard'
        ].filter(Boolean).join('\n'),
        attendees: bookingData.customerEmail ? [{ email: bookingData.customerEmail }] : []
      };

      // Call appropriate Firebase method based on action
      switch (action) {
        case 'create':
          return await FirebaseService.createCalendarEvent(businessId, eventData);
        case 'update':
          return await FirebaseService.updateCalendarEvent(businessId, eventData);
        case 'cancel':
        case 'delete':
          return await FirebaseService.deleteCalendarEvent(businessId, bookingData.htmlLink);
        default:
          throw new Error('Unknown calendar sync action: ' + action);
      }

    } catch (error) {
      console.error('Calendar sync failed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // ==========================================
  // BOOKING ANALYTICS & INSIGHTS (Existing methods)
  // ==========================================
  
  static async calculateBusinessAnalytics(businessId, timeframe = 'month') {
    try {
      const bookingsResult = await this.getAllBusinessBookings(businessId);
      
      if (!bookingsResult.success) {
        return {
          success: false,
          error: bookingsResult.error,
          analytics: null
        };
      }
      
      const allBookings = bookingsResult.bookings;
      
      // Filter by timeframe
      const now = new Date();
      let startDate, endDate;
      
      switch (timeframe) {
        case 'week':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
          endDate = now;
          break;
        case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          break;
        case 'quarter':
          const quarter = Math.floor(now.getMonth() / 3);
          startDate = new Date(now.getFullYear(), quarter * 3, 1);
          endDate = new Date(now.getFullYear(), quarter * 3 + 3, 0);
          break;
        case 'year':
          startDate = new Date(now.getFullYear(), 0, 1);
          endDate = new Date(now.getFullYear(), 11, 31);
          break;
        default:
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      }
      
      // Filter bookings by date range and valid status
      const bookings = allBookings.filter(booking => {
        const bookingDate = new Date(booking.bookingDate);
        return bookingDate >= startDate && 
               bookingDate <= endDate &&
               this.isBookingValid(booking);
      });
      
      // Calculate analytics
      const analytics = {
        // Basic counts
        totalBookings: bookings.length,
        confirmedBookings: bookings.filter(b => b.status === 'confirmed').length,
        rescheduledBookings: bookings.filter(b => b.status === 'rescheduled').length,
        
        // Revenue analytics
        totalRevenue: bookings.reduce((sum, booking) => sum + (booking.servicePrice || 0), 0),
        averageBookingValue: bookings.length > 0 ?
          bookings.reduce((sum, booking) => sum + (booking.servicePrice || 0), 0) / bookings.length : 0,
        
        // Customer analytics
        uniqueCustomers: new Set(bookings.map(b => b.customerId)).size,
        repeatCustomers: this.calculateRepeatCustomers(bookings),
        
        // Service analytics
        popularServices: this.calculatePopularServices(bookings),
        averageServiceDuration: bookings.length > 0 ?
          bookings.reduce((sum, booking) => sum + (booking.serviceDuration || 0), 0) / bookings.length : 0,
        
        // Time-based analytics
        timeframe: timeframe,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        
        // Communication estimates (based on booking patterns)
        estimatedWhatsAppMessages: bookings.length * 2.5,
        estimatedPhoneCalls: bookings.length * 0.3,
        
        lastUpdated: new Date().toISOString()
      };
      
      return {
        success: true,
        analytics: analytics
      };
      
    } catch (error) {
      console.error('Failed to calculate business analytics:', error);
      return {
        success: false,
        error: error.message,
        analytics: null
      };
    }
  }
  
  static calculateRepeatCustomers(bookings) {
    const customerBookingCounts = {};
    
    bookings.forEach(booking => {
      const customerId = booking.customerId;
      customerBookingCounts[customerId] = (customerBookingCounts[customerId] || 0) + 1;
    });
    
    return Object.values(customerBookingCounts).filter(count => count > 1).length;
  }
  
  static calculatePopularServices(bookings) {
    const serviceCounts = {};
    
    bookings.forEach(booking => {
      const serviceName = booking.serviceName;
      if (!serviceCounts[serviceName]) {
        serviceCounts[serviceName] = {
          name: serviceName,
          count: 0,
          totalRevenue: 0
        };
      }
      serviceCounts[serviceName].count++;
      serviceCounts[serviceName].totalRevenue += booking.servicePrice || 0;
    });
    
    return Object.values(serviceCounts)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }
  
  static async getRecentActivity(businessId, limit = 10) {
    try {
      const bookingsResult = await this.getAllBusinessBookings(businessId);
      
      if (!bookingsResult.success) {
        return {
          success: false,
          activities: []
        };
      }
      
      const bookings = bookingsResult.bookings
        .sort((a, b) => new Date(b.created_at || b.bookingDate) - new Date(a.created_at || a.bookingDate))
        .slice(0, limit);
      
      const activities = bookings.map(booking => {
        const timeAgo = this.getTimeAgo(new Date(booking.created_at || booking.bookingDate));
        
        return {
          id: booking.id,
          type: 'booking',
          message: `${booking.status === 'confirmed' ? 'New booking' : 'Booking updated'}: ${booking.serviceName} with ${booking.customerName}`,
          time: timeAgo,
          status: booking.status,
          customer: booking.customerName,
          service: booking.serviceName
        };
      });
      
      return {
        success: true,
        activities: activities
      };
      
    } catch (error) {
      console.error('Failed to get recent activity:', error);
      return {
        success: false,
        activities: []
      };
    }
  }
  
  // ==========================================
  // UTILITY METHODS
  // ==========================================
  
  static getTimeAgo(date) {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  }

    static formatTimeAgo(date) {
    const now = new Date();
    const past = new Date(date);
    const diffMs = now - past;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  }
  
  
  static formatCurrency(amount) {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount);
  }
  
  static formatDuration(minutes) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  }
  
  static isBookingValid(booking) {
    return booking && 
           (booking.status === 'confirmed' || booking.status === 'rescheduled') &&
           booking.serviceName &&
           booking.bookingDate;
  }
  // ==========================================
  // ADD THESE NEW CONFLICT-AWARE METHODS
  // ==========================================

  /**
   * Enhanced createBooking method with conflict resolution
   * This preserves your existing createBooking method completely unchanged
   */
  static async createBookingWithConflictResolution(businessId, bookingData, options = {}) {
    try {
      // Check if conflict resolution is enabled
      if (!ConflictResolutionService.CONFLICT_RESOLUTION_ENABLED || options.useConflictResolution === false) {
        // Fallback to original method (100% backward compatibility)
        return await this.createBooking(businessId, bookingData);
      }

      // Use atomic conflict resolution
      return await ConflictResolutionService.createBookingWithConflictResolution(
        businessId, 
        bookingData, 
        options
      );

    } catch (error) {
      // Enhanced error tracking
      ErrorTrackingService.captureError(error, {
        service: 'BookingService',
        method: 'createBookingWithConflictResolution',
        businessId,
        bookingData: {
          serviceName: bookingData.serviceName,
          customerName: bookingData.customerName,
          bookingDate: bookingData.bookingDate,
          time: bookingData.time
        },
        conflictResolutionEnabled: ConflictResolutionService.CONFLICT_RESOLUTION_ENABLED
      });

      throw error;
    }
  }

  /**
   * Enhanced updateBooking method with conflict checking
   */
  static async updateBookingWithConflictResolution(businessId, customerId, bookingId, updateData, options = {}) {
    try {
      // If conflict resolution disabled or no date/time changes, use original method
      if (!ConflictResolutionService.CONFLICT_RESOLUTION_ENABLED || 
          options.useConflictResolution === false ||
          (!updateData.bookingDate && !updateData.time)) {
        return await this.updateBooking(businessId, customerId, bookingId, updateData);
      }

      // Enhanced update with conflict checking
      return await BookingConflictEnhancer.updateBookingWithConflictCheck(
        businessId, 
        customerId, 
        bookingId, 
        updateData
      );

    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'BookingService',
        method: 'updateBookingWithConflictResolution',
        businessId,
        bookingId,
        customerId
      });

      throw error;
    }
  }

  /**
   * Batch create bookings with conflict resolution
   */
  static async createMultipleBookingsWithConflictResolution(businessId, bookingsArray, options = {}) {
    try {
      ErrorTrackingService.addBreadcrumb(
        'Batch booking creation with conflict resolution started',
        'booking',
        'info',
        { businessId, bookingCount: bookingsArray.length }
      );

      // Validate all bookings for conflicts first
      const validation = await BookingConflictEnhancer.validateBookingConflicts(
        businessId,
        bookingsArray
      );

      if (!validation.success) {
        return {
          success: false,
          error: 'Batch validation failed',
          validation
        };
      }

      const results = [];
      const conflicts = [];
      let successCount = 0;

      // Process each booking
      for (let i = 0; i < bookingsArray.length; i++) {
        const bookingData = bookingsArray[i];
        
        try {
          const result = await this.createBookingWithConflictResolution(
            businessId, 
            bookingData, 
            { ...options, batchIndex: i }
          );

          results.push({
            index: i,
            bookingData,
            result,
            success: result.success
          });

          if (result.success) {
            successCount++;
          } else if (result.conflicts) {
            conflicts.push({
              index: i,
              bookingData,
              conflicts: result.conflicts
            });
          }

        } catch (error) {
          results.push({
            index: i,
            bookingData,
            result: { success: false, error: error.message },
            success: false
          });
        }
      }

      ErrorTrackingService.addBreadcrumb(
        'Batch booking creation completed',
        'booking',
        'info',
        { 
          businessId, 
          totalBookings: bookingsArray.length,
          successCount,
          conflictCount: conflicts.length
        }
      );

      return {
        success: successCount > 0,
        results,
        conflicts,
        summary: {
          totalBookings: bookingsArray.length,
          successfulBookings: successCount,
          failedBookings: bookingsArray.length - successCount,
          conflictingBookings: conflicts.length
        },
        validation
      };

    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'BookingService',
        method: 'createMultipleBookingsWithConflictResolution',
        businessId,
        bookingCount: bookingsArray.length
      });

      return {
        success: false,
        error: error.message,
        results: [],
        conflicts: []
      };
    }
  }

  /**
   * Smart booking creation that automatically chooses the best method
   */
  static async createBookingSmart(businessId, bookingData, options = {}) {
    try {
      // Determine if conflict resolution should be used
      const useConflictResolution = options.forceConflictResolution === true || 
        (options.useConflictResolution !== false && 
         process.env.REACT_APP_ENABLE_CONFLICT_RESOLUTION !== 'false');

      if (useConflictResolution) {
        return await this.createBookingWithConflictResolution(businessId, bookingData, options);
      } else {
        return await this.createBooking(businessId, bookingData);
      }

    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'BookingService',
        method: 'createBookingSmart',
        businessId,
        options
      });

      throw error;
    }
  }

  // ==========================================
  // CONFLICT PREVENTION UTILITIES
  // ==========================================

  /**
   * Check for potential booking conflicts
   */
  static async checkBookingConflicts(businessId, dateTime, duration = 60, excludeBookingId = null) {
    try {
      return await ConflictResolutionService.checkTimeSlotAvailability(
        businessId,
        dateTime,
        duration,
        excludeBookingId
      );

    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'BookingService',
        method: 'checkBookingConflicts',
        businessId,
        dateTime,
        duration
      });

      return {
        isAvailable: true, // Default to available to prevent blocking
        conflicts: [],
        error: error.message
      };
    }
  }

  /**
   * Get alternative time slots when conflicts occur
   */
  static async getAlternativeTimeSlots(businessId, originalDate, duration = 60, count = 5) {
    try {
      return await ConflictResolutionService.suggestAlternativeTimeSlots(
        businessId,
        originalDate,
        duration,
        count
      );

    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'BookingService',
        method: 'getAlternativeTimeSlots',
        businessId,
        originalDate,
        duration
      });

      return [];
    }
  }

  /**
   * Validate booking request before creation
   */
  static async validateBookingData(businessId, bookingData) {
    try {
      return await ConflictResolutionService.validateBookingRequest(businessId, bookingData);

    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'BookingService',
        method: 'validateBookingData',
        businessId
      });

      return {
        isValid: true, // Default to valid to prevent blocking
        errors: [],
        warnings: [`Validation failed: ${error.message}`]
      };
    }
  }

  // ==========================================
  // INTEGRATION WITH EXISTING NOTIFICATION SYSTEM
  // ==========================================

  /**
   * Enhanced booking creation with automatic conflict notifications
   */
  static async createBookingWithNotifications(businessId, bookingData, options = {}) {
    try {
      // Create booking with conflict resolution
      const bookingResult = await this.createBookingWithConflictResolution(
        businessId, 
        bookingData, 
        options
      );

      // Handle notifications based on result
      if (bookingResult.success) {
        // Send success notification using your existing system
        try {
          await this.sendBookingNotifications('booking_created', {
            businessId,
            bookingId: bookingResult.bookingId,
            customerId: bookingResult.customerId,
            bookingData
          });
        } catch (notificationError) {
          console.warn('Booking notification failed:', notificationError);
          // Don't fail the booking creation for notification errors
        }
      } else if (bookingResult.conflicts) {
        // Send conflict notification
        try {
          await NotificationService.sendConflictAlert(businessId, {
            conflictCount: bookingResult.conflicts.length,
            requestedTime: `${bookingData.bookingDate} ${bookingData.time}`,
            customerName: bookingData.customerName,
            conflicts: bookingResult.conflicts,
            suggestedAlternatives: bookingResult.suggestedAlternatives
          });
        } catch (notificationError) {
          console.warn('Conflict notification failed:', notificationError);
        }
      }

      return bookingResult;

    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'BookingService',
        method: 'createBookingWithNotifications',
        businessId
      });

      throw error;
    }
  }

  // ==========================================
  // CALENDAR INTEGRATION WITH CONFLICT RESOLUTION
  // ==========================================

  /**
   * Enhanced calendar sync with conflict detection
   */
  static async syncBookingToCalendarWithConflictResolution(businessId, bookingData, action) {
    try {
      // First, check for calendar conflicts if this is a new/updated booking
      if (action === 'create' || action === 'update') {
        const conflicts = await this.checkBookingConflicts(
          businessId,
          bookingData.event_start,
          bookingData.duration_minutes || 60,
          bookingData.id
        );

        if (!conflicts.isAvailable && conflicts.conflicts.length > 0) {
          ErrorTrackingService.addBreadcrumb(
            'Calendar sync detected conflicts',
            'calendar',
            'warning',
            { 
              businessId, 
              bookingId: bookingData.id,
              conflictCount: conflicts.conflicts.length
            }
          );

          // Log conflicts but don't block calendar sync
          console.warn('Calendar sync proceeding despite conflicts:', conflicts.conflicts);
        }
      }

      // Proceed with original calendar sync
      return await this.syncBookingToCalendar(businessId, bookingData, action);

    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'BookingService',
        method: 'syncBookingToCalendarWithConflictResolution',
        businessId,
        bookingId: bookingData.id,
        action
      });

      // Fallback to original sync method
      return await this.syncBookingToCalendar(businessId, bookingData, action);
    }
  }

  // ==========================================
  // ANALYTICS AND MONITORING ENHANCEMENTS
  // ==========================================

  /**
   * Get booking analytics with conflict data
   */
  static async getBookingAnalyticsWithConflicts(businessId, timeframe = 'month') {
    try {
      // Get original analytics
      const analytics = await this.calculateBusinessAnalytics(businessId, timeframe);

      // Add conflict resolution statistics
      try {
        const conflictStats = await BookingConflictEnhancer.getConflictResolutionStats(
          businessId, 
          timeframe
        );

        if (analytics.success && conflictStats.success) {
          analytics.analytics.conflictResolution = conflictStats.stats;
        }
      } catch (conflictError) {
        console.warn('Failed to load conflict statistics:', conflictError);
      }

      return analytics;

    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'BookingService',
        method: 'getBookingAnalyticsWithConflicts',
        businessId,
        timeframe
      });

      // Fallback to original analytics
      return await this.calculateBusinessAnalytics(businessId, timeframe);
    }
  }

  // ==========================================
  // CONFIGURATION AND HEALTH CHECKS
  // ==========================================

  /**
   * Check conflict resolution system health
   */
  static async checkConflictResolutionHealth(businessId) {
    try {
      const health = {
        conflictResolutionEnabled: ConflictResolutionService.CONFLICT_RESOLUTION_ENABLED,
        lockTimeout: ConflictResolutionService.LOCK_TIMEOUT,
        maxRetryAttempts: ConflictResolutionService.MAX_RETRY_ATTEMPTS,
        strictTimeValidation: ConflictResolutionService.STRICT_TIME_VALIDATION,
        systemStatus: 'healthy',
        checks: {}
      };

      // Test conflict detection
      try {
        const testDate = new Date();
        testDate.setHours(testDate.getHours() + 24); // Tomorrow
        
        await ConflictResolutionService.checkTimeSlotAvailability(
          businessId,
          testDate.toISOString(),
          60
        );
        
        health.checks.conflictDetection = {
          status: 'healthy'
        };
      } catch (error) {
        health.checks.conflictDetection = {
          status: 'error',
          error: error.message
        };
        health.systemStatus = 'degraded';
      }

      // Test lock system
      try {
        const { BookingLockManager } = await import('../utils/BookingLockManager');
        const lockStats = await BookingLockManager.getLockStatistics(businessId);
        health.checks.lockSystem = {
          status: lockStats.success ? 'healthy' : 'error',
          activeLocks: lockStats.stats?.active || 0,
          error: lockStats.error
        };
      } catch (error) {
        health.checks.lockSystem = {
          status: 'error',
          error: error.message
        };
        health.systemStatus = 'degraded';
      }

      health.checkedAt = new Date().toISOString();
      return health;

    } catch (error) {
      return {
        conflictResolutionEnabled: false,
        systemStatus: 'error',
        error: error.message,
        checkedAt: new Date().toISOString()
      };
    }
  }

  /**
   * Reset conflict resolution system (admin function)
   */
  static async resetConflictResolutionSystem(businessId, options = {}) {
    try {
      const { BookingLockManager } = await import('../utils/BookingLockManager');

      ErrorTrackingService.addBreadcrumb(
        'Conflict resolution system reset initiated',
        'admin',
        'warning',
        { businessId, options }
      );

      const results = {
        lockCleanup: null,
        systemReset: false
      };

      // Clean up all locks for the business
      if (options.cleanupLocks !== false) {
        results.lockCleanup = await BookingLockManager.emergencyCleanup(
          businessId,
          'System reset'
        );
      }

      // Mark system as reset
      results.systemReset = true;
      results.resetAt = new Date().toISOString();

      ErrorTrackingService.addBreadcrumb(
        'Conflict resolution system reset completed',
        'admin',
        'info',
        { businessId, results }
      );

      return {
        success: true,
        results,
        message: 'Conflict resolution system reset successfully'
      };

    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'BookingService',
        method: 'resetConflictResolutionSystem',
        businessId,
        options
      });

      return {
        success: false,
        error: error.message
      };
    }
  }
}